import { MemberView } from './adapters';
import { MemberStage } from './tabData';
import { isFirstPass } from './passOrder';

/**
 * 회원 현황 목록의 순서 — **맨 위가 곧 오늘 처리할 사람.**
 *
 * ─────────────────────────────────────────────────────────────
 * 왜 화면에서 빼냈나
 * ─────────────────────────────────────────────────────────────
 * 정렬 규칙이 화면 안 `useMemo` 에 적혀 있으면 확인할 방법이 렌더뿐입니다.
 * 「첫 수강권을 앞으로 당기되 급한 순서를 뒤집지 않는다」 같은 규칙은
 * 눈으로 봐서는 확인이 안 됩니다 — 두 조건이 동시에 맞아야 하고,
 * 틀리면 원장님은 "왜 이 사람이 위에 있지"를 물을 뿐 원인을 못 찾습니다.
 * 그래서 규칙만 여기로 옮기고 화면은 부르기만 합니다.
 *
 * ─────────────────────────────────────────────────────────────
 * 🔴 첫 수강권 가중은 **보조 키**입니다 (2026-08-27 대표 결정 ③)
 * ─────────────────────────────────────────────────────────────
 * 첫 수강권은 「먼저 보여줄 이유」이지 「급한 이유」가 아닙니다.
 * 그래서 각 단계의 **첫 번째 급한 순서(주 키)가 같을 때만** 앞으로 당깁니다.
 * 주 키를 제치고 당기면 잔여 10회짜리 첫 수강권 회원이 잔여 0회 회원보다
 * 위에 서게 되고, 그 목록은 "위에서부터 연락하면 된다"는 뜻을 잃습니다.
 *
 * 순번을 확정할 수 없는 회원(재등록 유입·미분류)은 당기지 않습니다 —
 * `isFirstPass` 가 `basis === 'EXACT'` 만 받습니다. 모를 때 당기면
 * 이미 여러 번 끊은 회원이 첫 수강권 자리를 차지합니다.
 */

/** 모르는 값은 맨 뒤로 — "잔여 0회"로 취급하면 급한 회원을 밀어낸다 */
const last = (v: number | null | undefined): number => v ?? Number.POSITIVE_INFINITY;

/**
 * 같은 급이면 첫 수강권이 먼저.
 * 둘 다이거나 둘 다 아니면 0 — 그 아래 키가 순서를 정합니다.
 */
const byFirstPass = (a: MemberView, b: MemberView): number =>
    Number(isFirstPass(b.passOrder)) - Number(isFirstPass(a.passOrder));

/**
 * 그 단계의 목록을 급한 순으로 세운다.
 *
 * 원본 배열을 건드리지 않습니다(복사 후 정렬). `Array.prototype.sort` 는 안정
 * 정렬이라, 모든 키가 같으면 서버가 준 순서가 그대로 남습니다.
 */
export const sortMembersForStage = (list: MemberView[], stage: MemberStage): MemberView[] => {
    /*
     * 재등록 골든타임만 정렬 기준이 다르다.
     *
     * 다른 단계에서 맨 위에 와야 하는 사람은 "가장 급한 사람"이지만,
     * 이 단계에서 원장님이 찾는 사람은 **재등록을 꺼낼 만한 사람**이다.
     * 잔여가 0회라도 두 달째 안 나오는 회원에게 재등록 이야기를 꺼낼 수는 없다.
     * 최근까지 나온 회원이 성사 확률이 높다 — 대표 판단.
     *
     * 마지막 수업 기록이 없는 회원(null)은 "오늘 왔다"로 취급하면 맨 위로 올라와
     * 정작 연락할 사람을 밀어낸다. 모르는 값은 맨 뒤로 보낸다.
     */
    if (stage === 'GOLDEN') {
        return [...list].sort((a, b) => {
            const g = last(a.lastLessonDaysAgo) - last(b.lastLessonDaysAgo);
            if (g !== 0) return g;
            /* 같은 날 왔으면 첫 수강권부터 — 재등록이 갈리는 자리가 여기다 */
            const fp = byFirstPass(a, b);
            if (fp !== 0) return fp;
            // 그다음은 잔여가 적은 쪽이 급하다 (다른 단계와 같은 기준)
            return last(a.remain) - last(b.remain);
        });
    }

    return [...list].sort((a, b) => {
        /*
         * 급한 순서로 올린다.
         * 잔여를 모르는 회원은 "잔여 0회"로 취급하면 맨 위로 올라와
         * 정작 급한 회원을 밀어낸다. 모르는 값은 맨 뒤로 보낸다.
         */
        const r = last(a.remain) - last(b.remain);
        if (r !== 0) return r;
        /* 잔여가 같으면 첫 수강권부터 */
        const fp = byFirstPass(a, b);
        if (fp !== 0) return fp;
        return (a.paceDeltaPct ?? 0) - (b.paceDeltaPct ?? 0);
    });
};
